import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Users, ShieldCheck, ShieldAlert, FileText, ScrollText,
  Link2, Globe, Zap, AlertTriangle, Settings, LogOut, Building2, Briefcase,
  UserCog, DatabaseZap, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { useTheme } from '../ThemeContext';

const allNavItems = [
  {
    section: 'MAIN',
    items: [
      { path: '/',              label: 'Dashboard',     icon: LayoutDashboard, roles: ['ADMIN','COMPLIANCE_OFFICER','FUND_MANAGER','VIEWER'] },
      { path: '/fund-managers', label: 'Fund Managers', icon: Briefcase,       roles: ['ADMIN'] },
      { path: '/funds',         label: 'Funds',         icon: Building2,       roles: ['ADMIN','COMPLIANCE_OFFICER'] },
      { path: '/users',         label: 'Users',         icon: UserCog,         roles: ['ADMIN'] },
      { path: '/migration',     label: 'Migration',     icon: DatabaseZap,     roles: ['ADMIN'] },
      { path: '/investors',     label: 'Investors',     icon: Users,           roles: ['ADMIN','COMPLIANCE_OFFICER','FUND_MANAGER','VIEWER'] },
    ]
  },
  {
    section: 'COMPLIANCE',
    items: [
      { path: '/suitability',  label: 'Suitability', icon: ShieldCheck,  roles: ['ADMIN','COMPLIANCE_OFFICER'] },
      { path: '/aml',          label: 'AML Monitor', icon: ShieldAlert,  roles: ['ADMIN','COMPLIANCE_OFFICER'] },
      { path: '/documents',    label: 'Documents',   icon: FileText,     roles: ['ADMIN','COMPLIANCE_OFFICER'] },
      { path: '/audit-log',    label: 'Audit Log',   icon: ScrollText,   roles: ['ADMIN','COMPLIANCE_OFFICER'] },
      { path: '/hash-chain',   label: 'Hash Chain',  icon: Link2,        roles: ['ADMIN','COMPLIANCE_OFFICER'] },
    ]
  },
  {
    section: 'REGULATORY',
    items: [
      { path: '/regulatory',         label: 'Regulatory', icon: Globe,         roles: ['ADMIN','COMPLIANCE_OFFICER','FUND_MANAGER','VIEWER'] },
      { path: '/impact-assessments', label: 'Impact',     icon: Zap,           roles: ['ADMIN','COMPLIANCE_OFFICER','FUND_MANAGER','VIEWER'] },
      { path: '/incidents',          label: 'DORA',       icon: AlertTriangle, roles: ['ADMIN','COMPLIANCE_OFFICER','FUND_MANAGER','VIEWER'] },
    ]
  },
  {
    section: 'SETTINGS',
    items: [
      { path: '/settings', label: 'Settings', icon: Settings, roles: ['ADMIN','COMPLIANCE_OFFICER','FUND_MANAGER','VIEWER'] },
    ]
  },
];

const SIDEBAR_W     = 240;
const SIDEBAR_W_COL = 68;

const Sidebar = ({ onClose, sidebarOpen, isMobile, collapsed, onCollapse }) => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { theme } = useTheme();
  const user      = JSON.parse(localStorage.getItem('user') || '{}');

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const width = isMobile ? SIDEBAR_W : (collapsed ? SIDEBAR_W_COL : SIDEBAR_W);

  return (
    <div style={{
      position:      'fixed',
      top:           0,
      left:          0,
      bottom:        0,
      width,
      background:    theme.sidebarBg,
      transform:     isMobile && !sidebarOpen ? 'translateX(-100%)' : 'translateX(0)',
      transition:    'transform 0.25s ease, width 0.2s ease',
      zIndex:        100,
      display:       'flex',
      flexDirection: 'column',
      overflowY:     'auto',
      overflowX:     'hidden',
      borderRight:   `1px solid ${theme.sidebarBorder}`,
    }}>

      {/* Logo + collapse button */}
      <div style={{
        padding:      collapsed ? '20px 0' : '20px 16px',
        borderBottom: `1px solid ${theme.sidebarBorder}`,
        display:      'flex',
        alignItems:   'center',
        justifyContent: collapsed ? 'center' : 'space-between',
        minHeight:    64,
      }}>
        {!collapsed && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <span style={{ fontSize: 20, fontWeight: 800, color: theme.textPrimary, letterSpacing: '-0.5px' }}>Reg</span>
            <span style={{ fontSize: 20, fontWeight: 800, color: '#3B82F6', letterSpacing: '-0.5px' }}>Shield</span>
            </div>
            <p style={{ margin: '2px 0 0', fontSize: 9, color: theme.sidebarSection, letterSpacing: '0.12em', fontWeight: 600 }}>
              LUXEMBOURG REGTECH
            </p>
          </div>
        )}

        {collapsed && (
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: '#3B82F6',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ fontSize: 14, fontWeight: 900, color: 'white' }}>R</span>
          </div>
        )}

        {/* Collapse toggle — desktop only */}
        {!isMobile && (
          <button
            onClick={onCollapse}
            style={{
              background: theme.sidebarBgHover,
              border:     'none',
              borderRadius: 6,
              cursor:     'pointer',
              padding:    '4px',
              display:    'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color:      theme.sidebarText,
              flexShrink: 0,
            }}
          >
            {collapsed
              ? <ChevronRight size={14} />
              : <ChevronLeft  size={14} />
            }
          </button>
        )}

        {/* Mobile close */}
        {isMobile && (
          <button
            onClick={() => onClose && onClose()}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: theme.sidebarText, fontSize: 18, lineHeight: 1, padding: 4,
            }}
          >
            ✕
          </button>
        )}
      </div>

      {/* Nav */}
      <div style={{ flex: 1, padding: '8px 0', overflowY: 'auto' }}>
        {allNavItems.map(({ section, items }) => {
          const visible = items.filter(item => item.roles.includes(user.role));
          if (visible.length === 0) return null;
          return (
            <div key={section} style={{ marginBottom: 4 }}>
              {!collapsed && (
                <p style={{
                  margin:        '16px 16px 4px',
                  fontSize:      9,
                  fontWeight:    700,
                  color:         theme.sidebarSection,
                  letterSpacing: '0.12em',
                }}>
                  {section}
                </p>
              )}
              {collapsed && <div style={{ height: 12 }} />}

              {visible.map(({ path, label, icon: Icon }) => {
                const active = location.pathname === path;
                return (
                  <Link
                    key={path}
                    to={path}
                    onClick={onClose}
                    title={collapsed ? label : undefined}
                    style={{
                      display:        'flex',
                      alignItems:     'center',
                      gap:            collapsed ? 0 : 10,
                      padding:        collapsed ? '10px 0' : '9px 16px',
                      margin:         '1px 8px',
                      borderRadius:   7,
                      textDecoration: 'none',
                      justifyContent: collapsed ? 'center' : 'flex-start',
                      background:     active ? theme.sidebarBgActive : 'transparent',
                      color:          active ? theme.sidebarTextActive : theme.sidebarText,
                      fontSize:       13,
                      fontWeight:     active ? 600 : 400,
                      transition:     'all 0.15s',
                      borderLeft:     active && !collapsed ? '2px solid #3B82F6' : '2px solid transparent',
                      position:       'relative',
                    }}
                    onMouseEnter={e => {
                      if (!active) {
                        e.currentTarget.style.background = theme.sidebarBgHover;
                        e.currentTarget.style.color = theme.sidebarTextHover;
                      }
                    }}
                    onMouseLeave={e => {
                      if (!active) {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = theme.sidebarText;
                      }
                    }}
                  >
                    <Icon
                      size={16}
                      strokeWidth={active ? 2 : 1.75}
                      style={{ flexShrink: 0, color: active ? '#3B82F6' : 'inherit' }}
                    />
                    {!collapsed && label}
                  </Link>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* User info + logout */}
      <div style={{
        padding:   collapsed ? '12px 8px' : '12px 16px',
        borderTop: `1px solid ${theme.sidebarBorder}`,
      }}>
        {!collapsed && (
          <div style={{
            background:   theme.sidebarBgHover,
            borderRadius: 8,
            padding:      '10px 12px',
            marginBottom: 8,
          }}>
            <p style={{
              margin: 0, fontSize: 12, fontWeight: 600, color: 'white',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {user.full_name || user.email}
            </p>
            <p style={{
              margin: '2px 0 0', fontSize: 10, color: theme.sidebarText,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {user.email}
            </p>
            <span style={{
              display:       'inline-block',
              marginTop:     4,
              fontSize:      9,
              fontWeight:    700,
              background:    '#1D4ED8',
              color:         '#93C5FD',
              padding:       '2px 8px',
              borderRadius:  20,
              letterSpacing: '0.06em',
            }}>
              {user.role}
            </span>
          </div>
        )}

        <button
          onClick={logout}
          title={collapsed ? 'Sign out' : undefined}
          style={{
            width:          '100%',
            padding:        collapsed ? '10px 0' : '8px 12px',
            borderRadius:   7,
            background:     'transparent',
            border:         `1px solid ${theme.sidebarBorder}`,
            color:          theme.sidebarText,
            fontSize:       12,
            fontWeight:     500,
            cursor:         'pointer',
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'center',
            gap:            collapsed ? 0 : 6,
            transition:     'all 0.15s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = 'rgba(239,68,68,0.15)';
            e.currentTarget.style.borderColor = 'rgba(239,68,68,0.4)';
            e.currentTarget.style.color = '#FCA5A5';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = theme.sidebarBorder;
            e.currentTarget.style.color = theme.sidebarText;
          }}
        >
          <LogOut size={14} />
          {!collapsed && 'Sign out'}
        </button>
      </div>
    </div>
  );
};

export { SIDEBAR_W, SIDEBAR_W_COL };
export default Sidebar;